import type { ChartConfig } from '../../../types';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const MONTH_YEAR_KEY = 'Month-Year';

/**
 * If data has separate Year and Month columns, add a "Month-Year" column
 * (formatted as "Jan-2025") and return transformed data + config with xAxis set to it.
 * Also sorts rows by year then month so the line chart draws in order.
 * Returns null if no Year/Month columns found (caller should use original data/config).
 */
export function transformYearMonthForLine(
  data: Record<string, unknown>[],
  config: ChartConfig,
): { data: Record<string, unknown>[]; config: ChartConfig } | null {
  if (!data.length) return null;

  const keys = Object.keys(data[0]);
  const yearCol = keys.find((k) => k.toLowerCase() === 'year');
  const monthCol = keys.find((k) => k.toLowerCase() === 'month');
  if (!yearCol || !monthCol) return null;

  const sorted = [...data].sort((a, b) => {
    const ya = Number(a[yearCol] ?? 0);
    const yb = Number(b[yearCol] ?? 0);
    if (ya !== yb) return ya - yb;
    const ma = Number(a[monthCol] ?? 0);
    const mb = Number(b[monthCol] ?? 0);
    return ma - mb;
  });

  const transformed = sorted.map((row) => {
    const year = Number(row[yearCol] ?? 0);
    const month = Number(row[monthCol] ?? 1);
    const monthName = MONTH_NAMES[Math.max(0, Math.min(month - 1, 11))];
    return { ...row, [MONTH_YEAR_KEY]: `${monthName}-${year}` };
  });

  return {
    data: transformed,
    config: { ...config, xAxis: MONTH_YEAR_KEY },
  };
}
